import { RiCheckLine, RiCloseLine, RiDeleteBinFill, RiLayoutGridLine, RiListUnordered, RiLockFill, RiLockUnlockLine, RiPrinterFill, RiQrScan2Line } from '@remixicon/react'
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import { toast } from 'sonner'

import { bundlesService } from '@/api/bundles/bundles-service'
import { skidsService } from '@/api/skids/skids-service'
import type { Bundle } from '@/api/bundles/bundles-types'
import type { Detail, Order } from '@/api/orders/orders-types'
import { useItemsByAutoIds } from '@/api/items/items'
import { useDeleteSkid, useSkid, useSkids, useUpdateSkid } from '@/api/skids/skids'
import type {
  Skid,
  SkidListItem,
  SkidPaginatedResponse
} from '@/api/skids/skids-types'
import { DetailCard } from '@/components/common/detail/detail-card'
import {
  extractBundleIdFromInput,
  extractSkidLabelFromInput,
  resolveSkidInputToId
} from '@/utils/skid-input-resolve'
import { useBundleScanId, useDetailId } from '@/hooks/use-query-states'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { useSkidPanelKey } from '@/hooks/use-query-states'
import { cn } from '@/lib/utils'
import { usePrintStickers } from '@/store/print-stickers'
import { useScanWarningStore } from '@/store/scan-warning'
import { useSkidPanelHasHydrated, useSkidPanelStore } from '@/store/skid-panel'
import { forgetSkidLengthBump, useSkidLengthBump } from '@/hooks/use-skid-length-bump'
import {
  SKID_SIZES,
  type SkidSizeOption,
  formatPackagingSkidBadgeLabel,
  formatShippingUnitName,
  formatSkidSize,
  formatSkidSizeWithUnit,
  resizeSkidPayload,
  skidSizeOption,
  toBackendUnitName
} from '@/utils/order-skids'
import { formatWeight } from '@/utils/weight'

/**
 * Subscribe to the QueryClient cache so that consumers re-render whenever any
 * `['orders']` / `['order']` entry is added, updated, or removed. We read those
 * caches imperatively (via `getQueriesData`) instead of through `useQuery`, so
 * without this subscription the sheet would render bundles from a stale snapshot
 * after mutations like attach/detach/reset invalidated them.
 */
function useOrdersCacheVersion(queryClient: QueryClient): number {
  return useSyncExternalStore(
    (onStoreChange) => {
      const cache = queryClient.getQueryCache()
      return cache.subscribe((event) => {
        const k = event.query.queryKey?.[0]
        if (k === 'orders' || k === 'order') onStoreChange()
      })
    },
    () => queryClient.getQueryCache().getAll().length,
    () => 0
  )
}

/**
 * Collect every `Order` currently cached under any query key starting with ['orders'].
 * Handles both useQuery (`{ results }`) and useInfiniteQuery (`{ pages: [{ results }] }`)
 * shapes so we can reuse whatever the parent page already fetched instead of re-requesting.
 */
function collectCachedOrders(
  entries: Array<[unknown, unknown]>
): Order[] {
  const out: Order[] = []
  for (const [, data] of entries) {
    if (!data || typeof data !== 'object') continue
    const maybePages = (data as { pages?: unknown }).pages
    if (Array.isArray(maybePages)) {
      for (const page of maybePages) {
        const results = (page as { results?: Order[] })?.results
        if (Array.isArray(results)) out.push(...results)
      }
      continue
    }
    const results = (data as { results?: Order[] }).results
    if (Array.isArray(results)) out.push(...results)
  }
  return out
}

export type BundleGroup = {
  key: string
  detail: Detail | null
  bundles: Bundle[]
}

function groupBundlesByDetail(bundles: Bundle[], details: Detail[]): BundleGroup[] {
  const detailByAutoId = new Map(details.map((d) => [d.autoid, d]))
  const groups = new Map<string, BundleGroup>()
  for (const bundle of bundles) {
    const key = bundle.origin_item
    let group = groups.get(key)
    if (!group) {
      group = { key, detail: detailByAutoId.get(key) ?? null, bundles: [] }
      groups.set(key, group)
    }
    group.bundles.push(bundle)
  }
  return Array.from(groups.values())
}

function skidTypeLabel({ skid_type: t, label }: Pick<Skid, 'skid_type' | 'label'>): string {
  switch (t) {
    // A package names itself once the crew says what it is.
    case 'LHB':
      return label ?? 'Package'
    case 'SKI':
      return 'Skid'
    case 'MOS':
      return 'Multi order skid'
    case 'OUT':
      return 'Outside'
    default:
      return t
  }
}

function orderSubtitle(skid: Skid): string | null {
  const raw = skid.orders?.[0]
  if (!raw?.trim()) return null
  const inv = raw.split('-')[0]?.trim()
  return inv ? `Order #${inv}` : null
}

function bundleLabel(bundle: Bundle) {
  return String(bundle.id)
}

export const SkidDetailsSheet = () => {
  const [skidKey, setSkidKey] = useSkidPanelKey()
  const [bundleScanId, setBundleScanId] = useBundleScanId()
  const [detailId, setDetailId] = useDetailId()
  const queryClient = useQueryClient()
  const { setPrintOrder, setPrintSkid } = usePrintStickers()
  const panelOrder = useSkidPanelStore((s) => s.order)
  const setPanelOrder = useSkidPanelStore((s) => s.setOrder)
  const readOnly = useSkidPanelStore((s) => s.readOnly)
  const setReadOnly = useSkidPanelStore((s) => s.setReadOnly)
  const bulkScan = useSkidPanelStore((s) => s.bulkScan)
  const setBulkScan = useSkidPanelStore((s) => s.setBulkScan)
  const flashWarning = useScanWarningStore((s) => s.flash)
  const bumpSkidLength = useSkidLengthBump()
  // When a bundle-scan session is active, BundleScanSheet owns `?skid=<label>` processing
  // (consumes it as the shipping-unit assignment). Defer entirely so we don't steal the
  // user's flow by opening the skid sheet on top of the bundle.
  const bundleScanActive = useSkidPanelStore((s) => s.bundleScanActive)
  // `persist` rehydrates async, so right after an AHK reload (`?bundle=<id>` →
  // `?skid=<label>`) the store can transiently report `bundleScanActive == null`
  // before sessionStorage is read back. Without this gate the sheet would briefly
  // pop open on top of the bundle flow.
  const skidPanelHydrated = useSkidPanelHasHydrated()

  // The AHK helper navigates the browser to each scanned URL, which strips `?skid=` and
  // adds `?bundle=`. To survive that for the bulk-scan-bundles flow, we read the skid
  // identity from the store as a fallback when the URL is missing it.
  //
  // For the bulk-scan-LHBs flow (MOS/OUT) the AHK flips `?skid=<MOS>` to `?skid=<LHB>` —
  // both populated, but the new label is meant as an LHB attach action, not a navigation.
  // Pin `effectiveKey` to the bulk session's MOS/OUT label so the sheet doesn't drift to
  // the LHB; the URL-flip handler below consumes the incoming label as an attach.
  const incomingLhbScan =
    bulkScan?.mode === 'lhbs' &&
    skidKey != null &&
    skidKey.toLowerCase() !== bulkScan.skidLabel.toLowerCase()
      ? skidKey
      : null
  const effectiveKey = incomingLhbScan
    ? bulkScan!.skidLabel
    : skidKey ?? bulkScan?.skidLabel ?? null
  const open = effectiveKey != null && bundleScanActive == null && skidPanelHydrated

  // The URL param `?skid=<key>` is a skid label/name (preferred — this is what's encoded
  // in printed QR codes) OR a numeric id (kept for in-app navigation flexibility). Resolve
  // it to a numeric id once before all downstream lookups.
  const numericKey =
    effectiveKey != null && /^\d+$/.test(effectiveKey) ? Number.parseInt(effectiveKey, 10) : null

  const { data: lookupRes, isLoading: lookupLoading } = useQuery({
    queryKey: ['skids', 'lookup-by-name', effectiveKey],
    queryFn: () => skidsService.get({ name: effectiveKey!, limit: 1 }),
    enabled: open && numericKey == null && bulkScan == null
  })

  const skidId =
    bulkScan?.skidId ?? numericKey ?? (lookupRes?.results?.[0]?.id ?? null)

  const setSkidId = (next: number | null) => {
    if (next == null) {
      // Sheet close path: tear down the bulk-scan store too, otherwise the store would
      // keep `effectiveKey` non-null and the sheet would stay open after the user clicks
      // Close.
      setBulkScan(null)
      setSkidKey(null)
    } else {
      setSkidKey(String(next))
    }
  }

  const { data: skid, isLoading: skidLoading, isError } = useSkid(skidId ?? undefined)
  const resolveLoading =
    open && bulkScan == null && numericKey == null && lookupLoading
  const lookupNotFound =
    open &&
    bulkScan == null &&
    numericKey == null &&
    !lookupLoading &&
    (lookupRes?.results?.length ?? 0) === 0

  const isMultiOrder = skid?.skid_type === 'MOS' || skid?.skid_type === 'OUT'

  // Only MOS/OUT needs the list query — LHBs span orders and must be resolvable for scans/nesting.
  // SKI/LHB views read bundles directly from the cached orders (`order.details[*].bundles`).
  const { data: skidsPage } = useSkids(
    { limit: 500, offset: 0, skid_type__in: 'LHB,MOS,OUT' },
    { enabled: open && isMultiOrder }
  )

  const ordersCacheVersion = useOrdersCacheVersion(queryClient)
  const cachedOrders = useMemo<Order[]>(() => {
    const fromList = collectCachedOrders(
      queryClient.getQueriesData({ queryKey: ['orders'] })
    )
    const fromSingle = queryClient
      .getQueriesData({ queryKey: ['order'] })
      .map(([, data]) => data as Order | undefined)
      .filter((o): o is Order => !!o && typeof o === 'object' && 'details' in o)
    return [...fromList, ...fromSingle]
  }, [queryClient, ordersCacheVersion])

  const cachedDetails = useMemo<Detail[]>(() => {
    const byAutoId = new Map<string, Detail>()
    for (const order of cachedOrders) {
      for (const detail of order.details ?? []) {
        if (detail?.autoid) byAutoId.set(detail.autoid, detail)
      }
    }
    return Array.from(byAutoId.values())
  }, [cachedOrders])

  const bundles = useMemo<Bundle[]>(() => {
    if (skidId == null) return []
    const seen = new Set<number>()
    const out: Bundle[] = []
    for (const order of cachedOrders) {
      for (const detail of order.details ?? []) {
        for (const bundle of detail.bundles ?? []) {
          if (bundle.skid_id === skidId && !seen.has(bundle.id)) {
            seen.add(bundle.id)
            out.push(bundle)
          }
        }
      }
    }
    return out
  }, [cachedOrders, skidId])

  // Display name (`BDL-…` for an LHB). Sticker QRs for LHBs use the order link, not this.
  const titleLabel = skid != null ? formatShippingUnitName(skid as SkidListItem) : ''

  const updateSkid = useUpdateSkid()

  const resetLhbsMutation = useMutation({
    mutationFn: (id: number) => skidsService.resetLhbs(id),
    meta: {
      successMessage: 'Shipping unit cleared',
      errorMessage: 'Failed to reset shipping unit'
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['skids'] })
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['order'] })
      queryClient.invalidateQueries({ queryKey: ['items'] })
    }
  })

  const [scanOpen, setScanOpen] = useState(false)
  const [scanValue, setScanValue] = useState('')
  const scanInputRef = useRef<HTMLInputElement>(null)
  const [bundleScanValue, setBundleScanValue] = useState('')
  const bundleScanInputRef = useRef<HTMLInputElement>(null)

  // Auto-arm bulk-scan-bundles for SKI/LHB so a fresh skid sheet open (e.g. from a QR scan)
  // immediately accepts bundle scans without an extra click. Disabled for read-only views and
  // when the skid is already closed.
  const canBulkScanBundles =
    !readOnly &&
    skid != null &&
    !skid.is_complete &&
    (skid.skid_type === 'SKI' || skid.skid_type === 'LHB')

  // Same idea for MOS/OUT: scanning a multi-order skid or outside-rack QR should drop the
  // packager straight into the LHB scan input without a manual "Scan packages" click.
  const canBulkScanLhbs =
    !readOnly && skid != null && !skid.is_complete && isMultiOrder

  useEffect(() => {
    // Wait for the skid to resolve before deciding bulk mode — `canBulkScan*` flags
    // already require `skid != null`, but leaving bulkScan untouched while it loads
    // preserves the session across transient lookup/refetch states.
    if (skid == null || skidId == null) return
    const label = formatPackagingSkidBadgeLabel(skid as SkidListItem)
    if (canBulkScanBundles) {
      if (bulkScan?.skidId !== skidId || bulkScan.mode !== 'bundles') {
        setBulkScan({ skidId, skidLabel: label, mode: 'bundles' })
      }
      return
    }
    if (canBulkScanLhbs) {
      if (bulkScan?.skidId !== skidId || bulkScan.mode !== 'lhbs') {
        setBulkScan({ skidId, skidLabel: label, mode: 'lhbs' })
      }
      return
    }
    // Skid resolved but doesn't qualify (closed / wrong type / readOnly) — drop bulk mode.
    if (bulkScan != null) setBulkScan(null)
  }, [canBulkScanBundles, canBulkScanLhbs, skidId, skid, bulkScan, setBulkScan])

  // Auto-open the LHB scan bar when a MOS/OUT skid resolves. Keyed on `skidId` so it fires
  // once per skid open — if the user clicks Cancel we don't re-arm on the same skid.
  useEffect(() => {
    if (canBulkScanLhbs) setScanOpen(true)
  }, [canBulkScanLhbs, skidId])

  useEffect(() => {
    if (!open) {
      setScanOpen(false)
      setScanValue('')
      setBundleScanValue('')
      setPanelOrder(null)
      setReadOnly(false)
      setBulkScan(null)
    }
  }, [open, setPanelOrder, setReadOnly, setBulkScan])

  // Bundle QRs carry `?detail=` and usePackagingUrlPersistence restores it after every
  // AHK reload. The detail sidebar mounts later (lazy page + order fetch), lands on top
  // of this sheet and its focus trap steals the scan input. The skid is the operator's
  // context once this sheet is open — drop the detail.
  useEffect(() => {
    if (open && detailId) setDetailId(null)
  }, [open, detailId, setDetailId])

  useEffect(() => {
    if (!scanOpen) return
    const focusInput = () => {
      const el =
        scanInputRef.current ??
        (document.querySelector(
          'input[data-skid-bulk-scan="1"]'
        ) as HTMLInputElement | null)
      if (el) {
        el.focus()
        el.select?.()
      }
    }
    const ids = [50, 150, 350, 600].map((ms) => window.setTimeout(focusInput, ms))
    return () => ids.forEach((id) => window.clearTimeout(id))
  }, [scanOpen])

  // Same focus-retry pattern as `scanOpen` above — Radix focus trap and sheet animation
  // both fight us, so we attempt focus across several frames after bulk mode arms.
  useEffect(() => {
    if (!canBulkScanBundles) return
    const focusInput = () => {
      const el =
        bundleScanInputRef.current ??
        (document.querySelector(
          'input[data-skid-bulk-scan-bundles="1"]'
        ) as HTMLInputElement | null)
      if (el) {
        el.focus()
        el.select?.()
      }
    }
    const ids = [50, 150, 350, 600].map((ms) => window.setTimeout(focusInput, ms))
    return () => ids.forEach((id) => window.clearTimeout(id))
  }, [canBulkScanBundles, skidId])

  const assignBundleToSkidMutation = useMutation({
    mutationFn: async ({
      bundleId
    }: {
      bundleId: number
      bundleLabel: string
      groupKey: string | null
      /** Length of the scanned part, in inches — used to grow the skid in `onSuccess`. */
      detailLength: number | null
    }) => {
      return bundlesService.multiUpdate({
        ids: [bundleId],
        skid_id: skidId!,
        is_complete: true
      })
    },
    meta: {
      errorMessage: 'Failed to attach bundle'
    },
    onSuccess: (_data, vars) => {
      toast.success(`Bundle ${vars.bundleLabel} attached`)
      bumpSkidLength(skidId, skid?.length, vars.detailLength)
      // Only the scanned detail stays open in list view, highlighted for 2s.
      if (vars.groupKey) {
        setExpandedGroups({ [vars.groupKey]: true })
        setHighlightedGroup(vars.groupKey)
        if (highlightTimerRef.current) window.clearTimeout(highlightTimerRef.current)
        highlightTimerRef.current = window.setTimeout(() => setHighlightedGroup(null), 2000)
      }
      setBundleScanValue('')
      queryClient.invalidateQueries({ queryKey: ['skids'] })
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['order'] })
      queryClient.invalidateQueries({ queryKey: ['items'] })
      requestAnimationFrame(() => bundleScanInputRef.current?.focus())
    },
    onError: (_err, vars) => {
      // Failed attach must stay retryable — drop the id from the dedupe set so a
      // re-scan of the same bundle isn't silently swallowed.
      processedBundleIdsRef.current.delete(vars.bundleId)
      setBundleScanValue('')
      requestAnimationFrame(() => bundleScanInputRef.current?.focus())
    }
  })

  // Dedupe bundle ids we've already submitted within this bulk-scan session — guards
  // against the input + URL paths both firing for the same scan, and against re-runs of
  // this effect when the cache invalidates.
  const processedBundleIdsRef = useRef<Set<number>>(new Set())

  const tryAssignBundle = (bundleId: number) => {
    if (!canBulkScanBundles || skidId == null) return
    if (processedBundleIdsRef.current.has(bundleId)) return
    if (assignBundleToSkidMutation.isPending) return

    // Find the bundle in the cached orders so we can validate state before mutating.
    // Capture the owning order alongside it for the cross-order guard below.
    let foundBundle: Bundle | null = null
    let foundOrder: Order | null = null
    for (const order of cachedOrders) {
      for (const detail of order.details ?? []) {
        for (const b of detail.bundles ?? []) {
          if (b.id === bundleId) {
            foundBundle = b
            foundOrder = order
            break
          }
        }
        if (foundBundle) break
      }
      if (foundBundle) break
    }

    // Cross-order guard: SKI/LHB are single-order skids. If the operator scans a
    // bundle from a different order while a SKI/LHB bulk session is live, hand
    // the scan back to BundleScanSheet (which validates against the bundle's
    // home order via `resolveSkidInputToId({ orderAutoid })`) instead of
    // silently mutating `bundle.skid_id` to the wrong order's skid. MOS/OUT
    // span orders so this check is correctly scoped to SKI/LHB only.
    const isSingleOrderSkid = skid?.skid_type === 'SKI' || skid?.skid_type === 'LHB'
    if (
      foundOrder != null &&
      isSingleOrderSkid &&
      !(skid!.orders ?? []).some((o) => o === foundOrder!.autoid)
    ) {
      flashWarning(
        `Bundle ${bundleId} belongs to a different order — opening it directly.`,
        { title: 'Different order' }
      )
      // Tear down the bulk session; the next render routes through
      // BundleScanSheet (still has `?bundle=<id>`).
      setBulkScan(null)
      setSkidKey(null)
      setBundleScanValue('')
      return
    }

    const bundleLabelStr = String(bundleId)
    if (foundBundle?.skid_id === skidId && foundBundle.is_complete === true) {
      toast.info(`Bundle ${bundleLabelStr} is already on this shipping unit.`, {
        id: `bulk-dup-${bundleId}-${skidId}`
      })
      processedBundleIdsRef.current.add(bundleId)
      setBundleScanValue('')
      return
    }

    processedBundleIdsRef.current.add(bundleId)
    const scannedDetail = foundBundle?.origin_item
      ? allDetails.find((d) => d.autoid === foundBundle.origin_item)
      : undefined
    assignBundleToSkidMutation.mutate({
      bundleId,
      bundleLabel: bundleLabelStr,
      groupKey: foundBundle?.origin_item ?? null,
      detailLength: scannedDetail?.heightd ?? null
    })
  }

  // Reset the dedupe set when the bulk-scan target changes.
  useEffect(() => {
    processedBundleIdsRef.current = new Set()
  }, [skidId])

  // Path 1 — value typed/scanned directly into the bulk-scan input. Fires before the
  // AHK helper has a chance to navigate, since our debounce (100ms) beats the script's
  // ~250ms path. URL form ("https://.../packaging?bundle=42&...") and bare "42" / "BUN-42"
  // all flow through extractBundleIdFromInput.
  useEffect(() => {
    if (!canBulkScanBundles) return
    if (!bundleScanValue.trim()) return
    if (assignBundleToSkidMutation.isPending) return
    const t = window.setTimeout(() => {
      const id = extractBundleIdFromInput(bundleScanValue)
      if (id != null) tryAssignBundle(id)
      else {
        toast.error('Could not read bundle id from scan.', { id: `bulk-bundle-bad-${bundleScanValue}` })
        setBundleScanValue('')
      }
    }, 100)
    return () => window.clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bundleScanValue, canBulkScanBundles])

  // Path 2 — AHK navigated the browser to the bundle URL after the scan. We notice the
  // resulting `?bundle=<id>` and process it here, then strip it and restore `?skid=` so
  // the sheet survives for the next scan in the bulk session.
  //
  // Wait for `skid` to load before firing — when AHK does a full-page reload (Ctrl+L
  // navigation), the persisted bulkScan rehydrates instantly but the `useSkid` query
  // is still in flight. Without this gate `tryAssignBundle` would early-return on
  // `!canBulkScanBundles` (which requires `skid != null`) while we still strip
  // `?bundle=`, silently dropping the scan.
  useEffect(() => {
    if (bulkScan == null) return
    if (bundleScanId == null) return
    if (skid == null) return
    tryAssignBundle(bundleScanId)
    setBundleScanId(null)
    if (skidKey == null) setSkidKey(bulkScan.skidLabel)
    // Refresh `setAt` so an actively-scanning session stays live — the persisted
    // TTL would otherwise expire mid-flow on a long bulk-scan run.
    setBulkScan({
      skidId: bulkScan.skidId,
      skidLabel: bulkScan.skidLabel,
      mode: bulkScan.mode
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bundleScanId, bulkScan?.skidId, skid])

  // The bulk-scan store keeps the sheet open across AHK-driven URL flips (skid → bundle →
  // skid). If the user genuinely navigates away — neither `?skid=` nor `?bundle=` set —
  // the store would otherwise pin the sheet open on every page. Drop bulk after a short
  // grace period so legitimate transients (e.g. nuqs writing two params sequentially)
  // don't trigger a false drop.
  useEffect(() => {
    if (bulkScan == null) return
    if (skidKey != null || bundleScanId != null) return
    const t = window.setTimeout(() => setBulkScan(null), 200)
    return () => window.clearTimeout(t)
  }, [skidKey, bundleScanId, bulkScan, setBulkScan])

  const attachLhbMutation = useMutation({
    mutationFn: async ({ id }: { id: number; label: string }) =>
      skidsService.update(id, { mos_id: skidId! }),
    meta: {
      errorMessage: 'Failed to attach package'
    },
    // Fire a per-scan success toast with the LHB label here (not via meta) so the
    // user sees exactly which LHB landed when they scan several in a row.
    onSuccess: (_data, vars) => {
      toast.success(`${vars.label} attached`)
      setScanValue('')
      queryClient.invalidateQueries({ queryKey: ['skids'] })
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['order'] })
      queryClient.invalidateQueries({ queryKey: ['items'] })
      requestAnimationFrame(() => scanInputRef.current?.focus())
    },
    onError: () => {
      setScanValue('')
      requestAnimationFrame(() => scanInputRef.current?.focus())
    }
  })

  const tryAttachLhb = async (raw: string) => {
    const skidInput = extractSkidLabelFromInput(raw)
    if (!skidInput) {
      setScanValue('')
      return
    }

    // Resolve the scanned LHB with a targeted server lookup instead of matching
    // against a capped client-side page. There are >2k LHB/MOS/OUT skids, so a
    // bounded list silently omits any LHB outside the newest window and reports a
    // false "wrong skid" on a perfectly valid sticker. `name=` is an exact match;
    // numeric input (manual entry) resolves by id.
    let candidates: SkidListItem[]
    try {
      candidates = /^\d+$/.test(skidInput)
        ? [await skidsService.getById(Number.parseInt(skidInput, 10))]
        : (await skidsService.get({ name: toBackendUnitName(skidInput), limit: 2 })).results
    } catch {
      const msg = 'Could not look up the scanned package — try again.'
      toast.error(msg, { id: 'mos-scan-lookup-fail' })
      flashWarning(msg, { title: 'Scan failed' })
      setScanValue('')
      return
    }

    const resolved = resolveSkidInputToId(skidInput, candidates, {
      expect: 'lhb-container'
    })
    if (!resolved.ok) {
      toast.error(resolved.message, { id: `mos-scan-${resolved.code}` })
      flashWarning(resolved.message, { title: 'Wrong skid' })
      setScanValue('')
      return
    }
    if (resolved.skid_id == null) return

    const lhb = resolved.skid ?? candidates.find((s) => s.id === resolved.skid_id)
    if (!lhb) {
      toast.error('Package data not loaded yet — wait a moment and retry.')
      setScanValue('')
      return
    }

    const label = formatShippingUnitName(lhb)
    if (lhb.mos_id != null && lhb.mos_id !== skidId) {
      let parentLabel = `#${lhb.mos_id}`
      try {
        parentLabel = formatPackagingSkidBadgeLabel(await skidsService.getById(lhb.mos_id))
      } catch {
        // Keep the `#<id>` fallback when the parent skid can't be fetched.
      }
      const msg = `${label} is already on ${parentLabel}. Detach it there first.`
      toast.error(msg)
      flashWarning(msg, { title: 'Package already attached' })
      setScanValue('')
      return
    }
    if (lhb.mos_id === skidId) {
      toast.info(`${label} is already on this shipping unit.`)
      setScanValue('')
      return
    }
    attachLhbMutation.mutate({ id: lhb.id, label })
  }

  useEffect(() => {
    if (!scanOpen || !scanValue.trim() || attachLhbMutation.isPending) return
    const t = window.setTimeout(() => void tryAttachLhb(scanValue), 120)
    return () => window.clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanValue, scanOpen])

  // URL-flip path for the bulk-scan-LHBs flow: AHK navigates `?skid=<MOS>` →
  // `?skid=<LHB>`. `effectiveKey` already pins the sheet to the MOS via bulkScan, so
  // here we just consume the incoming label as an attach scan and snap `?skid=` back
  // to the MOS label so the next scan in the bulk session keeps working. No
  // `isPending` guard — successive scans should each fire their own mutation; the
  // attach mutation tolerates parallel calls and de-dupes via the LHB's `mos_id`
  // pre-check inside `tryAttachLhb`.
  //
  // Wait for `skid` to load before firing — when AHK does a full-page reload, the
  // persisted bulkScan rehydrates instantly but `tryAttachLhb` needs `skidId` from
  // the loaded `skid` to set `mos_id` on the mutation. The scanned LHB itself is
  // resolved via a targeted lookup inside `tryAttachLhb`, so no list is required.
  useEffect(() => {
    if (incomingLhbScan == null || bulkScan == null) return
    if (skid == null) return
    void tryAttachLhb(incomingLhbScan)
    setSkidKey(bulkScan.skidLabel)
    // Refresh `setAt` so an actively-scanning session stays live — the persisted
    // TTL would otherwise expire mid-flow on a long bulk-scan run.
    setBulkScan({
      skidId: bulkScan.skidId,
      skidLabel: bulkScan.skidLabel,
      mode: bulkScan.mode
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingLhbScan, bulkScan?.skidId, skid])

  const unassignBundleMutation = useMutation({
    mutationFn: async (bundle: Bundle) =>
      bundlesService.multiUpdate({ ids: [bundle.id], skid_id: null }),
    meta: {
      successMessage: 'Bundle unassigned',
      errorMessage: 'Failed to unassign bundle'
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['skids'] })
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['order'] })
      queryClient.invalidateQueries({ queryKey: ['items'] })
    }
  })

  const detachLhbMutation = useMutation({
    mutationFn: async (lhbId: number) => skidsService.update(lhbId, { mos_id: null }),
    meta: {
      successMessage: 'Package detached',
      errorMessage: 'Failed to detach package'
    },
    // Optimistically drop `mos_id` from the LHB in every cached paginated list under `['skids', ...]`
    // so the nested-LHB list updates the moment the user clicks Detach. We skip detail queries
    // (`['skids', 'detail', id]`) because those store a single Skid, not a paginated response.
    onMutate: async (lhbId) => {
      await queryClient.cancelQueries({ queryKey: ['skids'] })
      const snapshot = queryClient.getQueriesData<SkidPaginatedResponse>({
        queryKey: ['skids']
      })
      queryClient.setQueriesData<SkidPaginatedResponse | undefined>(
        { queryKey: ['skids'] },
        (old) => {
          if (!old || !Array.isArray((old as { results?: unknown }).results)) {
            return old
          }
          return {
            ...old,
            results: old.results.map((s) =>
              s.id === lhbId ? { ...s, mos_id: null } : s
            )
          }
        }
      )
      return { snapshot }
    },
    onError: (_err, _vars, ctx) => {
      if (!ctx?.snapshot) return
      for (const [key, data] of ctx.snapshot) {
        queryClient.setQueryData(key, data)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['skids'] })
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['order'] })
      queryClient.invalidateQueries({ queryKey: ['items'] })
    }
  })

  const nestedLhbs = useMemo(() => {
    if (!isMultiOrder || skidId == null) return []
    return (skidsPage?.results ?? [])
      .filter((s) => s.skid_type === 'LHB' && s.mos_id === skidId)
      .sort((a, b) => a.id - b.id)
  }, [isMultiOrder, skidId, skidsPage?.results])

  // Bundles in scope come from either the cached orders (SKI/LHB) or the nested LHBs (MOS/OUT).
  // Either way the per-bundle `origin_item` is the Detail autoid we need to render the bundle group.
  const neededAutoIds = useMemo(() => {
    const ids = new Set<string>()
    for (const b of bundles) if (b.origin_item) ids.add(b.origin_item)
    for (const lhb of nestedLhbs) {
      for (const b of lhb.bundles ?? []) if (b.origin_item) ids.add(b.origin_item)
    }
    return ids
  }, [bundles, nestedLhbs])

  const cachedAutoIds = useMemo(
    () => new Set(cachedDetails.map((d) => d.autoid).filter(Boolean) as string[]),
    [cachedDetails]
  )

  const missingAutoIds = useMemo(
    () => Array.from(neededAutoIds).filter((id) => !cachedAutoIds.has(id)),
    [neededAutoIds, cachedAutoIds]
  )

  const { data: fetchedDetails, isFetching: detailsFetching } = useItemsByAutoIds(missingAutoIds)

  const allDetails = useMemo<Detail[]>(() => {
    if (!fetchedDetails?.results?.length) return cachedDetails
    const byId = new Map<string, Detail>()
    for (const d of cachedDetails) if (d.autoid) byId.set(d.autoid, d)
    for (const d of fetchedDetails.results) if (d.autoid) byId.set(d.autoid, d)
    return Array.from(byId.values())
  }, [cachedDetails, fetchedDetails?.results])

  const detailsLoading = missingAutoIds.length > 0 && detailsFetching

  const bundleGroups = useMemo(() => groupBundlesByDetail(bundles, allDetails), [bundles, allDetails])

  // Skid weight is just the tally of what's on it; EBMS leaves `weight` empty on some
  // bundles, so `null` means "nothing to add up" rather than 0 lb.
  const skidWeight = useMemo(() => {
    const all = isMultiOrder ? nestedLhbs.flatMap((lhb) => lhb.bundles ?? []) : bundles
    let total = 0
    let hasWeight = false
    for (const bundle of all) {
      if (bundle.weight == null) continue
      hasWeight = true
      total += bundle.weight
    }
    return hasWeight ? total : null
  }, [isMultiOrder, nestedLhbs, bundles])

  const skidSize = skid ? formatSkidSize(skid) : null

  // Packaging went back to full cards by default; the list stays one click away.
  const [listView, setListView] = useState(false)
  // Which detail groups are expanded in list view. A scan collapses everything else and
  // flashes the scanned group for a moment so the operator's eye lands on it.
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({})
  const [highlightedGroup, setHighlightedGroup] = useState<string | null>(null)
  const highlightTimerRef = useRef<number | null>(null)

  useEffect(
    () => () => {
      if (highlightTimerRef.current) window.clearTimeout(highlightTimerRef.current)
    },
    []
  )

  // Group keys are detail autoids, which repeat across the skids of one order — without
  // this reset a group expanded (or highlighted) on the previous skid stays open here.
  useEffect(() => {
    setExpandedGroups({})
    setHighlightedGroup(null)
    if (highlightTimerRef.current) window.clearTimeout(highlightTimerRef.current)
  }, [skidId])
  // Picked width while resizing; `null` means the picker is closed.
  const [resizeTo, setResizeTo] = useState<SkidSizeOption | null>(null)
  useEffect(() => setResizeTo(null), [skidId])
  // A closed skid is packed as it is: reopened first to change it.
  const canResize = !readOnly && skid?.skid_type === 'SKI' && skid.is_complete !== true
  const currentSizeOption = skid ? skidSizeOption(skid) : null

  // Resizes the skid in place, so its bundles stay on it and only the label has to be
  // reprinted — opened right away, like after creating a skid.
  const resizeSkidMutation = useMutation({
    mutationFn: (next: SkidSizeOption) =>
      skidsService.update(
        skidId!,
        resizeSkidPayload(
          skid!,
          next,
          bundleGroups.map((g) => g.detail?.heightd)
        )
      ),
    meta: {
      errorMessage: 'Failed to change skid size',
      invalidatesQuery: ['skids', 'boxes', 'orders', 'items']
    },
    onSuccess: (updated) => {
      forgetSkidLengthBump(updated.id)
      setResizeTo(null)
      toast.success('Skid size changed')
      handlePrintQrSticker(formatSkidSize(updated))
    }
  })

  const [resetConfirmOpen, setResetConfirmOpen] = useState(false)
  const [reopenConfirmOpen, setReopenConfirmOpen] = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)

  const deleteSkid = useDeleteSkid()
  const canDelete =
    skid != null &&
    skid.skid_type !== 'OUT' &&
    (isMultiOrder ? nestedLhbs.length === 0 : bundles.length === 0)

  const confirmDelete = () => {
    if (skidId == null) return
    deleteSkid.mutate(skidId, {
      onSuccess: () => {
        setDeleteConfirmOpen(false)
        setSkidId(null)
      }
    })
  }

  const toggleShippingUnitComplete = () => {
    if (skidId == null || skid == null) return
    // Closing is one-click on purpose (commit 11074d8); only ask before re-opening
    // a CLOSED unit, since that means undoing a deliberate "done packing" signal.
    if (skid.is_complete === true) {
      setReopenConfirmOpen(true)
      return
    }
    updateSkid.mutate({
      id: skidId,
      payload: { is_complete: true }
    })
  }

  const confirmReopen = () => {
    if (skidId == null) return
    updateSkid.mutate(
      { id: skidId, payload: { is_complete: false } },
      { onSuccess: () => setReopenConfirmOpen(false) }
    )
  }

  const handlePrintQrSticker = (size = skidSize) => {
    if (!skid) return

    // MOS / Outside Rack — no order context, print a simplified sticker.
    if (skid.skid_type === 'MOS' || skid.skid_type === 'OUT') {
      setPrintSkid({
        skidId: skid.id,
        skidLabel: titleLabel,
        skidType: skid.skid_type
      })
      return
    }

    const skidKeys = skid.orders ?? []
    const matchesSkid = (o: Order) => {
      const autoids = new Set(
        (o.details ?? []).map((d) => d.autoid).filter(Boolean) as string[]
      )
      return skidKeys.some(
        (s) =>
          s === o.autoid ||
          autoids.has(s) ||
          s === o.invoice ||
          s.startsWith(`${o.invoice}-`)
      )
    }

    let order: Order | null = panelOrder ?? null
    if (!order) order = cachedOrders.find(matchesSkid) ?? null

    if (!order) {
      toast.error('Order for this shipping unit is not loaded on this page.')
      return
    }

    const orderSkids = (order.skids ?? [])
      .filter((s) => s.skid_type === 'SKI' || s.skid_type === 'LHB')
      .sort((a, b) => a.id - b.id)
    const idx = orderSkids.findIndex((s) => s.id === skid.id)
    setPrintOrder({
      order,
      labels: ['shipping'],
      skidId: skid.id,
      skidLabel: titleLabel,
      skidIndex: idx >= 0 ? idx + 1 : undefined,
      skidTotal: orderSkids.length || undefined,
      skidSize: size,
      skidWeight
    })
  }

  const isComplete = skid?.is_complete === true
  const primaryOrder = skid ? orderSubtitle(skid) : null

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) setSkidId(null)
      }}
    >
      <SheetContent
        side='right'
        onOpenAutoFocus={(e) => {
          e.preventDefault()
          requestAnimationFrame(() => scanInputRef.current?.focus())
        }}
        className={cn(
          'flex w-full max-w-[30.75rem] flex-col gap-0 overflow-hidden p-0 sm:max-w-[30.75rem]',
          '[&_.sheet-close]:top-3.5'
        )}
      >
        {!open ? null : resolveLoading || skidLoading || detailsLoading ? (
          <div className='flex flex-1 flex-col gap-4 p-6'>
            <Skeleton className='h-8 w-48' />
            <Skeleton className='h-4 w-64' />
            <Skeleton className='h-24 w-full' />
            <Skeleton className='h-24 w-full' />
          </div>
        ) : lookupNotFound || isError || !skid ? (
          <div className='p-6'>
            <SheetHeader className='p-0'>
              <SheetTitle className='text-lg'>Skid</SheetTitle>
            </SheetHeader>
            <p className='text-grey-600 mt-4 text-sm'>
              {lookupNotFound
                ? `No shipping unit named "${skidKey}" was found. Check the sticker and try again.`
                : 'Could not load this skid.'}
            </p>
            <Button
              type='button'
              variant='outline'
              className='mt-6 w-full'
              onClick={() => setSkidId(null)}
            >
              Close
            </Button>
          </div>
        ) : (
          <>
            <SheetHeader className='border-grey-200 space-y-3 border-b px-4 pt-5 pb-3'>
              <div className='flex items-start justify-between gap-3 pr-8'>
                <div className='min-w-0'>
                  <SheetTitle className='text-black-200 text-2xl font-bold tracking-tight'>
                    {titleLabel}
                  </SheetTitle>
                  <p className='text-grey-600 mt-1 text-sm'>
                    Skid
                    {primaryOrder ? ` — ${primaryOrder}` : null}
                  </p>
                </div>
                <span
                  className={cn(
                    'inline-flex shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold',
                    isComplete
                      ? 'bg-grey-200 text-grey-700'
                      : 'bg-green-100 text-green-200'
                  )}
                >
                  {isComplete ? 'CLOSED' : 'OPEN'}
                </span>
              </div>
              <div className='bg-grey-100 text-grey-700 flex flex-wrap gap-x-6 gap-y-1 rounded-md px-3 py-2 text-xs'>
                {isMultiOrder ? (
                  <>
                    <span>
                      <span className='text-grey-500'>Packages: </span>
                      {nestedLhbs.length}
                    </span>
                    <span>
                      <span className='text-grey-500'>Total bundles: </span>
                      {nestedLhbs.reduce((n, l) => n + (l.bundles?.length ?? 0), 0)}
                    </span>
                  </>
                ) : (
                  <span>
                    <span className='text-grey-500'>Total bundles: </span>
                    {bundles.length}
                  </span>
                )}
                {skidSize ? (
                  <span>
                    <span className='text-grey-500'>Size: </span>
                    {formatSkidSizeWithUnit(skidSize)}
                  </span>
                ) : null}
                {skidWeight != null ? (
                  <span>
                    <span className='text-grey-500'>Weight: </span>
                    {formatWeight(skidWeight)} lb
                  </span>
                ) : null}
                <span>
                  <span className='text-grey-500'>Type: </span>
                  {skidTypeLabel(skid)}
                </span>
                {canResize && resizeTo == null ? (
                  <button
                    type='button'
                    className='text-blue-300 ml-auto font-semibold hover:underline'
                    onClick={() => setResizeTo(currentSizeOption ?? SKID_SIZES[0])}
                  >
                    Change size
                  </button>
                ) : null}
              </div>
              {canResize && resizeTo != null ? (
                <div className='flex items-center gap-0.5 rounded-lg border border-blue-200 bg-blue-50/80 px-1 py-0.5'>
                  <span className='text-grey-600 pl-1 text-[11px] leading-none'>Width</span>
                  {SKID_SIZES.map((option) => (
                    <Button
                      key={option.key}
                      type='button'
                      variant={resizeTo.key === option.key ? 'default' : 'ghost'}
                      size='sm'
                      className='h-7 px-1.5 text-xs tabular-nums'
                      disabled={resizeSkidMutation.isPending}
                      onClick={() => setResizeTo(option)}
                      aria-pressed={resizeTo.key === option.key}
                    >
                      {option.label}
                    </Button>
                  ))}
                  <Button
                    type='button'
                    variant='ghost'
                    size='icon-sm'
                    className='ml-auto size-7 text-green-200'
                    disabled={resizeSkidMutation.isPending || resizeTo.key === currentSizeOption?.key}
                    onClick={() => resizeSkidMutation.mutate(resizeTo)}
                    title='Change size and reprint the label'
                    aria-label='Confirm'
                  >
                    <RiCheckLine className='size-4' />
                  </Button>
                  <Button
                    type='button'
                    variant='ghost'
                    size='icon-sm'
                    className='text-grey-500 size-7'
                    disabled={resizeSkidMutation.isPending}
                    onClick={() => setResizeTo(null)}
                    aria-label='Cancel'
                  >
                    <RiCloseLine className='size-4' />
                  </Button>
                </div>
              ) : null}
            </SheetHeader>

            <ScrollArea className='min-h-0 flex-1'>
              <div className='px-4 py-3'>
                {skid.skid_type === 'OUT' ? (
                  <p className='text-grey-600 mb-3 rounded-md border border-dashed border-grey-300 bg-grey-100/80 px-2 py-1.5 text-xs'>
                    Outside Rack resets at midnight. Use Reset to clear assignments when the physical rack is
                    empty.
                  </p>
                ) : null}
                {isMultiOrder && scanOpen ? (
                  <div className='mb-3 rounded-md border border-blue-200 bg-blue-50/60 p-3'>
                    <div className='mb-2 flex items-center justify-between'>
                      <span className='text-blue-300 text-sm font-semibold'>
                        Scan packages
                      </span>
                      <Button
                        type='button'
                        variant='outline'
                        size='sm'
                        className='h-7 px-2 text-xs'
                        onClick={() => {
                          setScanOpen(false)
                          setScanValue('')
                        }}
                      >
                        <RiCloseLine className='size-3.5' />
                        Cancel
                      </Button>
                    </div>
                    <Input
                      ref={(el) => {
                        scanInputRef.current = el
                        if (el) requestAnimationFrame(() => el.focus())
                      }}
                      autoFocus
                      value={scanValue}
                      onChange={(e) => setScanValue(e.target.value)}
                      placeholder='Scan package QR'
                      className='h-9 font-mono'
                      data-skid-bulk-scan='1'
                      disabled={attachLhbMutation.isPending}
                    />
                    <p className='text-grey-500 mt-2 text-xs'>
                      Each scanned package attaches instantly. Bundles can’t be scanned here —
                      attach them to a package first.
                    </p>
                  </div>
                ) : null}
                {canBulkScanBundles ? (
                  <div className='mb-3 rounded-md border border-blue-200 bg-blue-50/60 p-3'>
                    <div className='mb-2 flex items-center gap-2'>
                      <RiQrScan2Line className='size-4 text-blue-300' />
                      <span className='text-blue-300 text-sm font-semibold'>
                        Scan bundles
                      </span>
                    </div>
                    <Input
                      ref={(el) => {
                        bundleScanInputRef.current = el
                        if (el) requestAnimationFrame(() => el.focus())
                      }}
                      autoFocus
                      value={bundleScanValue}
                      onChange={(e) => setBundleScanValue(e.target.value)}
                      placeholder='Scan bundle QR'
                      className='h-9 font-mono'
                      data-skid-bulk-scan-bundles='1'
                      disabled={assignBundleToSkidMutation.isPending}
                    />
                    <p className='text-grey-500 mt-2 text-xs'>
                      Each scanned bundle attaches to {titleLabel} and is marked complete.
                    </p>
                  </div>
                ) : null}
                {isMultiOrder ? (
                  <div>
                    <div className='mb-2 flex items-center justify-between gap-2'>
                      <h3 className='text-grey-800 text-sm font-semibold'>
                        Packages on this shipping unit
                      </h3>
                      <Button
                        type='button'
                        variant='outline'
                        size='sm'
                        className='h-7 shrink-0 px-2 text-xs'
                        onClick={() => setListView((prev) => !prev)}
                      >
                        {listView ? (
                          <RiLayoutGridLine className='size-3.5' />
                        ) : (
                          <RiListUnordered className='size-3.5' />
                        )}
                        {listView ? 'Cards' : 'List'}
                      </Button>
                    </div>
                    <div className='space-y-3'>
                      {nestedLhbs.length === 0 ? (
                        <div className='text-grey-500 border-grey-200 rounded-md border px-3 py-6 text-center text-sm'>
                          No packages attached yet.
                        </div>
                      ) : (
                        nestedLhbs.map((lhb) => {
                          const lhbLabel = formatShippingUnitName(lhb)
                          const lhbOrder = lhb.orders?.[0]?.split('-')[0]?.trim() ?? '—'
                          const lhbBundles = lhb.bundles ?? []
                          const lhbGroups = groupBundlesByDetail(lhbBundles, allDetails)
                          return (
                            <div
                              key={lhb.id}
                              className='border-grey-200 overflow-hidden rounded-md border'
                            >
                              <div className='bg-grey-100 flex items-center justify-between gap-2 px-3 py-2'>
                                <div className='min-w-0'>
                                  <div className='text-blue-300 truncate font-mono text-sm font-semibold'>
                                    {lhbLabel}
                                  </div>
                                  <div className='text-grey-600 truncate text-xs'>
                                    Order #{lhbOrder} · {lhbBundles.length} bundle
                                    {lhbBundles.length === 1 ? '' : 's'}
                                  </div>
                                </div>
                                {readOnly ? null : (
                                  <Button
                                    type='button'
                                    variant='outline'
                                    size='sm'
                                    className='border-red-200 text-red-300 h-7 shrink-0 px-2 text-xs'
                                    disabled={detachLhbMutation.isPending}
                                    onClick={() => detachLhbMutation.mutate(lhb.id)}
                                  >
                                    Detach
                                  </Button>
                                )}
                              </div>
                              {lhbGroups.length > 0 ? (
                                <div className='space-y-2.5 p-2.5'>
                                  {lhbGroups.map((group) => (
                                    <BundleGroupCard
                                      key={group.key}
                                      group={group}
                                      collapsible={listView}
                                      expanded={expandedGroups[group.key] ?? false}
                                      highlighted={highlightedGroup === group.key}
                                      onExpandedChange={(next) =>
                                        setExpandedGroups((prev) => ({
                                          ...prev,
                                          [group.key]: next
                                        }))
                                      }
                                    />
                                  ))}
                                </div>
                              ) : null}
                            </div>
                          )
                        })
                      )}
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className='mb-2 flex items-center justify-between gap-2'>
                      <h3 className='text-grey-800 text-sm font-semibold'>
                        Bundles in this shipping unit
                      </h3>
                      <Button
                        type='button'
                        variant='outline'
                        size='sm'
                        className='h-7 shrink-0 px-2 text-xs'
                        onClick={() => setListView((prev) => !prev)}
                      >
                        {listView ? (
                          <RiLayoutGridLine className='size-3.5' />
                        ) : (
                          <RiListUnordered className='size-3.5' />
                        )}
                        {listView ? 'Cards' : 'List'}
                      </Button>
                    </div>
                    {bundles.length === 0 ? (
                      <div className='text-grey-500 border-grey-200 rounded-md border px-3 py-6 text-center text-sm'>
                        No bundles assigned yet.
                      </div>
                    ) : (
                      <div className='space-y-2.5'>
                        {bundleGroups.map((group) => (
                          <BundleGroupCard
                            key={group.key}
                            group={group}
                            collapsible={listView}
                            expanded={expandedGroups[group.key] ?? false}
                            highlighted={highlightedGroup === group.key}
                            onExpandedChange={(next) =>
                              setExpandedGroups((prev) => ({ ...prev, [group.key]: next }))
                            }
                            onUnassignBundle={(bundle) =>
                              unassignBundleMutation.mutate(bundle)
                            }
                            unassignDisabled={unassignBundleMutation.isPending}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </ScrollArea>

            <SheetFooter className='border-grey-200 bg-grey-100/80 mt-0 flex-col gap-2 border-t px-4 py-3'>
              {readOnly ? null : (() => {
                const contentsEmpty = isMultiOrder ? nestedLhbs.length === 0 : bundles.length === 0
                const contentsWord = isMultiOrder ? 'packages' : 'bundles'
                const disableClose = !isComplete && contentsEmpty
                const Icon = isComplete ? RiLockFill : RiLockUnlockLine
                return (
                  <label
                    className={cn(
                      'flex w-full cursor-pointer items-center justify-between gap-3 rounded-md border px-3 py-2 transition-colors',
                      isComplete
                        ? 'border-grey-300 bg-grey-200 text-grey-700'
                        : 'border-green-200 bg-green-100 text-green-300',
                      (updateSkid.isPending || disableClose) && 'cursor-not-allowed opacity-60'
                    )}
                    title={disableClose ? `Add ${contentsWord} before closing this shipping unit` : undefined}
                  >
                    <span className='flex items-center gap-2'>
                      <Icon className='size-4' />
                      <span className='text-sm font-semibold'>
                        {isComplete ? 'Closed' : 'Open — close when packed'}
                      </span>
                    </span>
                    <Switch
                      checked={isComplete}
                      disabled={updateSkid.isPending || disableClose}
                      onCheckedChange={toggleShippingUnitComplete}
                    />
                  </label>
                )
              })()}
              {!readOnly && isMultiOrder && !scanOpen && !isComplete ? (
                <Button
                  type='button'
                  variant='outline'
                  className='w-full justify-center gap-2'
                  onClick={() => setScanOpen(true)}
                >
                  <RiQrScan2Line className='size-4' />
                  Scan packages
                </Button>
              ) : null}
              {!readOnly && isMultiOrder && nestedLhbs.length > 0 ? (
                <Button
                  type='button'
                  variant='outline'
                  className='border-red-200 text-red-300 w-full'
                  disabled={resetLhbsMutation.isPending}
                  onClick={() => setResetConfirmOpen(true)}
                >
                  {skid.skid_type === 'OUT' ? 'Reset Outside Rack' : 'Reset all packages'}
                </Button>
              ) : null}
              <Button
                type='button'
                variant='outline'
                className='w-full justify-center gap-2'
                onClick={() => handlePrintQrSticker()}
              >
                <RiPrinterFill className='size-4' />
                Print QR sticker
              </Button>

              {!readOnly && canDelete ? (
                <Button
                  type='button'
                  variant='outline'
                  className='border-red-200 text-red-300 w-full justify-center gap-2'
                  disabled={deleteSkid.isPending}
                  onClick={() => setDeleteConfirmOpen(true)}
                >
                  <RiDeleteBinFill className='size-4' />
                  Delete {skidTypeLabel(skid)}
                </Button>
              ) : null}
              <Button
                type='button'
                variant='outline'
                className='bg-grey-200 border-grey-300 w-full'
                onClick={() => setSkidId(null)}
              >
                Close
              </Button>
            </SheetFooter>
          </>
        )}
      </SheetContent>
      <AlertDialog
        open={resetConfirmOpen}
        onOpenChange={setResetConfirmOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {skid?.skid_type === 'OUT' ? 'Reset Outside Rack?' : 'Detach all packages?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {skid?.skid_type === 'OUT'
                ? 'Remove all packages from this Outside Rack? Their bundles return to awaiting shipping unit on their orders.'
                : `Remove all ${nestedLhbs.length} package${nestedLhbs.length === 1 ? '' : 's'} from this multi-order skid? Their bundles return to awaiting shipping unit on their orders.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button
              type='button'
              variant='outline'
              className='border-red-200 text-red-300'
              disabled={resetLhbsMutation.isPending}
              onClick={() => {
                if (skidId == null) return
                resetLhbsMutation.mutate(skidId, {
                  onSuccess: () => setResetConfirmOpen(false)
                })
              }}
            >
              {resetLhbsMutation.isPending
                ? 'Resetting…'
                : skid?.skid_type === 'OUT'
                  ? 'Reset'
                  : 'Detach all'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog
        open={deleteConfirmOpen}
        onOpenChange={(next) => {
          if (!next) setDeleteConfirmOpen(false)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {skid ? skidTypeLabel(skid) : 'shipping unit'}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {titleLabel ? `${titleLabel} will be permanently removed.` : 'This shipping unit will be permanently removed.'}{' '}
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button
              type='button'
              variant='outline'
              className='border-red-200 text-red-300'
              disabled={deleteSkid.isPending}
              onClick={confirmDelete}
            >
              {deleteSkid.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog
        open={reopenConfirmOpen}
        onOpenChange={(next) => {
          if (!next) setReopenConfirmOpen(false)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reopen shipping unit?</AlertDialogTitle>
            <AlertDialogDescription>
              This unit was marked closed. Reopening it allows new bundles or packages to be
              attached again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button
              type='button'
              disabled={updateSkid.isPending}
              onClick={confirmReopen}
            >
              {updateSkid.isPending ? 'Reopening…' : 'Reopen'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Sheet>
  )
}

interface BundleGroupCardProps {
  group: BundleGroup
  onUnassignBundle?: (bundle: Bundle) => void
  unassignDisabled?: boolean
  collapsible?: boolean
  expanded?: boolean
  onExpandedChange?: (next: boolean) => void
  highlighted?: boolean
}

export const BundleGroupCard = ({
  group,
  onUnassignBundle,
  unassignDisabled,
  collapsible,
  expanded,
  onExpandedChange,
  highlighted
}: BundleGroupCardProps) => {
  const showActions = !!onUnassignBundle
  // Narrower fixed columns on a phone so the bundle label keeps a readable share.
  const gridTemplate = showActions
    ? 'grid-cols-[minmax(0,1fr)_3rem_2.5rem_3.5rem_1.75rem] gap-2 sm:grid-cols-[minmax(0,1fr)_4.5rem_3rem_4rem_2rem] sm:gap-3'
    : 'grid-cols-[minmax(0,1fr)_3rem_2.5rem_3.5rem] gap-2 sm:grid-cols-[minmax(0,1fr)_4.5rem_3rem_4rem] sm:gap-3'

  const totalQty = group.bundles.reduce((sum, b) => sum + (b.quantity ?? 0), 0)
  const totalWeight = group.bundles.reduce(
    (sum, b) => sum + (b.weight ?? 0),
    0
  )
  const hasWeight = group.bundles.some((b) => b.weight != null)
  const completeCount = group.bundles.filter((b) => b.is_complete).length

  const bundleList = (
    <div className='border-grey-200 bg-white-100 overflow-hidden rounded-md border'>
      <div
        className={cn(
          'bg-grey-100 text-grey-600 border-grey-200 grid items-center border-b px-3 py-2 text-[11px] font-semibold tracking-wide uppercase',
          gridTemplate
        )}
      >
        <span>Bundle</span>
        <span className='text-center'>Complete</span>
        <span className='text-right'>Qty</span>
        <span className='text-right'>Weight</span>
        {showActions ? <span className='sr-only'>Actions</span> : null}
      </div>
      {group.bundles.map((bundle, index) => {
        const bundleWeight = formatWeight(bundle.weight)
        return (
        <div
          key={bundle.id}
          className={cn(
            'border-grey-200 grid items-center px-3 py-2 text-sm',
            index > 0 ? 'border-t' : '',
            gridTemplate
          )}
        >
          <div className='min-w-0'>
            <div className='text-blue-200 truncate font-semibold tabular-nums'>
              {bundleLabel(bundle)}
            </div>
            {bundle.name ? (
              <div className='text-grey-500 truncate text-xs'>{bundle.name}</div>
            ) : null}
          </div>
          <div className='flex items-center justify-center'>
            {bundle.is_complete ? (
              <RiCheckLine
                className='text-green-200 size-4'
                aria-label='Complete'
              />
            ) : (
              <RiCloseLine
                className='text-grey-400 size-4'
                aria-label='Open'
              />
            )}
          </div>
          <div className='text-grey-800 text-right tabular-nums'>
            {bundle.quantity ?? '-'}
          </div>
          <div className='text-grey-700 text-right text-xs tabular-nums'>
            {bundleWeight != null ? `${bundleWeight} lb` : '—'}
          </div>
          {showActions ? (
            <button
              type='button'
              aria-label='Unassign bundle'
              title='Unassign'
              disabled={unassignDisabled}
              onClick={() => onUnassignBundle?.(bundle)}
              className='text-grey-500 hover:text-red-300 hover:border-red-200 inline-flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-md border border-transparent disabled:cursor-not-allowed disabled:opacity-50'
            >
              <RiCloseLine className='size-4' />
            </button>
          ) : null}
        </div>
        )
      })}
      {group.bundles.length > 1 ? (
        <div
          className={cn(
            'bg-grey-100 text-grey-700 border-grey-200 grid items-center border-t px-3 py-1.5 text-xs font-medium',
            gridTemplate
          )}
        >
          <span className='text-grey-500'>
            {completeCount}/{group.bundles.length} complete
          </span>
          <span />
          <span className='text-right tabular-nums'>{totalQty}</span>
          <span className='text-right tabular-nums'>
            {hasWeight ? `${formatWeight(totalWeight)} lb` : '—'}
          </span>
          {showActions ? <span /> : null}
        </div>
      ) : null}
    </div>
  )

  if (!group.detail) {
    return (
      <div className='border-grey-200 rounded-md border p-2.5'>
        <div className='text-grey-600 mb-2 text-xs'>
          Detail {group.key} not loaded
        </div>
        {bundleList}
      </div>
    )
  }

  return (
    <DetailCard
      detail={group.detail}
      allowedControls={[]}
      hideEdit
      hideMachinesInfo
      hideViewToggle
      collapsible={collapsible}
      expanded={expanded}
      onExpandedChange={onExpandedChange}
      className={highlighted ? 'bg-blue-50 outline-blue-300' : ''}
    >
      {bundleList}
    </DetailCard>
  )
}
